import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  LinearProgress,
  Container,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  PeopleAlt as PeopleAltIcon,
  MeetingRoom as MeetingRoomIcon,
  AttachMoney as AttachMoneyIcon,
  Timeline as TimelineIcon,
  BarChart as BarChartIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Computer as ComputerIcon,
  Refresh as RefreshIcon,
  DateRange as DateRangeIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  StarBorder as StarBorderIcon,
  AutoGraph as AutoGraphIcon,
  Analytics as AnalyticsIcon,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart as RechartsBarChart,
  Bar,
  AreaChart,
  Area,
} from "recharts";
import {
  blue,
  green,
  red,
  amber,
  purple,
  teal,
  orange,
  indigo,
  grey,
  pink,
  cyan,
} from "@mui/material/colors";
import useAdminContext from "../../store/contexts/AdminContext";

const AnalyticsDashboard = () => {
  const {
    authState: { loading, error },
    getUserAnalyticsApiCall,
    getRoomAnalyticsApiCall,
    getPaymentAnalyticsApiCall,
    getDashboardAnalyticsApiCall,
    getSystemHealthMetricsApiCall,
    getLoginActivityAnalyticsApiCall,
    clearErrors,
  } = useAdminContext();

  // State for analytics data
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [roomAnalytics, setRoomAnalytics] = useState(null);
  const [paymentAnalytics, setPaymentAnalytics] = useState(null);
  const [dashboardAnalytics, setDashboardAnalytics] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [loginActivity, setLoginActivity] = useState(null);

  // Filter states
  const [dateRange, setDateRange] = useState({
    dateFrom: "",
    dateTo: "",
    preset: "7d",
  });
  const [selectedAnalytics, setSelectedAnalytics] = useState("overview");

  useEffect(() => {
    clearErrors();
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dateRange.preset !== "custom") {
      const { dateFrom, dateTo } = getPresetDateRange(dateRange.preset);
      setDateRange((prev) => ({ ...prev, dateFrom, dateTo }));
      if (selectedAnalytics !== "overview") {
        loadSpecificAnalytics(selectedAnalytics, dateFrom, dateTo);
      } else {
        loadAnalytics();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.preset]);

  const getPresetDateRange = (preset) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let dateFrom = new Date(today);

    switch (preset) {
      case "7d":
        dateFrom.setDate(today.getDate() - 7);
        break;
      case "30d":
        dateFrom.setDate(today.getDate() - 30);
        break;
      case "90d":
        dateFrom.setDate(today.getDate() - 90);
        break;
      case "1y":
        dateFrom.setFullYear(today.getFullYear() - 1);
        break;
      default:
        dateFrom.setDate(today.getDate() - 7);
    }

    return {
      dateFrom: dateFrom.toISOString().split("T")[0],
      dateTo: today.toISOString().split("T")[0],
    };
  };

  const loadAnalytics = async () => {
    try {
      const [dashboardData, systemData] = await Promise.all([
        getDashboardAnalyticsApiCall(dateRange.dateFrom, dateRange.dateTo),
        getSystemHealthMetricsApiCall(),
      ]);

      if (dashboardData) setDashboardAnalytics(dashboardData);
      if (systemData) setSystemHealth(systemData);
    } catch (error) {
      console.error("Error loading overview analytics:", error);
    }
  };

  const loadSpecificAnalytics = async (
    type,
    customDateFrom = null,
    customDateTo = null
  ) => {
    const dateFrom = customDateFrom || dateRange.dateFrom;
    const dateTo = customDateTo || dateRange.dateTo;

    try {
      switch (type) {
        case "users":
          const userData = await getUserAnalyticsApiCall(dateFrom, dateTo);
          if (userData) setUserAnalytics(userData);
          break;
        case "rooms":
          const roomData = await getRoomAnalyticsApiCall(dateFrom, dateTo);
          if (roomData) setRoomAnalytics(roomData);
          break;
        case "payments":
          const paymentData = await getPaymentAnalyticsApiCall(
            dateFrom,
            dateTo
          );
          if (paymentData) setPaymentAnalytics(paymentData);
          break;
        case "login":
          const loginData = await getLoginActivityAnalyticsApiCall(
            dateFrom,
            dateTo
          );
          if (loginData) setLoginActivity(loginData);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(`Error loading ${type} analytics:`, error);
    }
  };

  const handleAnalyticsChange = (type) => {
    setSelectedAnalytics(type);
    if (type !== "overview") {
      loadSpecificAnalytics(type);
    }
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange((prev) => ({ ...prev, [field]: value, preset: "custom" }));
  };

  const handleApplyDateRange = () => {
    if (selectedAnalytics !== "overview") {
      loadSpecificAnalytics(selectedAnalytics);
    } else {
      loadAnalytics();
    }
  };

  const handleRefresh = () => {
    if (selectedAnalytics === "overview") {
      loadAnalytics();
    } else {
      loadSpecificAnalytics(selectedAnalytics);
    }
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    return `${parseFloat(value || 0).toFixed(1)}%`;
  };

  // Enhanced StatCard with beautiful animations and gradients
  const StatCard = ({
    title,
    value,
    icon,
    color,
    trend,
    trendValue,
    subtitle,
    progress,
  }) => (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        background: `linear-gradient(135deg, ${color}08 0%, ${color}03 100%)`,
        border: `1px solid ${color}20`,
        borderRadius: 4,
        position: "relative",
        overflow: "hidden",
        transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        "&:hover": {
          transform: "translateY(-8px)",
          boxShadow: `0 20px 40px ${color}30`,
          border: `1px solid ${color}40`,
          "& .stat-icon": {
            transform: "scale(1.1) rotate(5deg)",
          },
          "& .stat-value": {
            transform: "scale(1.05)",
          },
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${color} 0%, ${color}80 100%)`,
          borderRadius: "16px 16px 0 0",
        },
      }}
    >
      <CardContent sx={{ p: 3, position: "relative" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
          <Avatar
            className="stat-icon"
            sx={{
              bgcolor: color,
              width: 56,
              height: 56,
              mr: 2,
              boxShadow: `0 8px 24px ${color}40`,
              border: "3px solid white",
              transition: "all 0.3s ease",
            }}
          >
            {icon}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              className="stat-value"
              variant="h4"
              sx={{
                fontWeight: 800,
                color: "#1a1a1a",
                fontSize: { xs: "1.5rem", sm: "2rem" },
                lineHeight: 1.2,
                transition: "all 0.3s ease",
              }}
            >
              {typeof value === "number" ? value.toLocaleString() : value}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                sx={{ color: "#666", display: "block" }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#424242",
              fontSize: "1rem",
              mb: 0.5,
            }}
          >
            {title}
          </Typography>

          {progress !== undefined && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  flex: 1,
                  height: 6,
                  borderRadius: 3,
                  bgcolor: `${color}20`,
                  "& .MuiLinearProgress-bar": {
                    bgcolor: color,
                    borderRadius: 3,
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: color, fontWeight: 600 }}
              >
                {progress}%
              </Typography>
            </Box>
          )}
        </Box>

        {trend && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {trend === "up" ? (
              <TrendingUpIcon sx={{ fontSize: 18, color: green[600] }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 18, color: red[600] }} />
            )}
            <Typography
              variant="body2"
              sx={{
                color: trend === "up" ? green[700] : red[700],
                fontWeight: 700,
                fontSize: "0.85rem",
              }}
            >
              {trendValue}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const CHART_COLORS = [
    blue[500],
    green[500],
    amber[500],
    red[500],
    purple[500],
    teal[500],
    pink[500],
    cyan[500],
  ];

  const renderOverview = () => {
    if (loading) {
      return (
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 12,
              bgcolor: "white",
              borderRadius: 4,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            }}
          >
            <CircularProgress size={80} thickness={4} sx={{ mb: 3 }} />
            <Typography variant="h6" color="text.secondary">
              Loading analytics data...
            </Typography>
          </Box>
        </Container>
      );
    }

    return (
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          {/* Quick Stats */}
          {dashboardAnalytics?.quick_stats && (
            <>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  title="Total Users"
                  value={dashboardAnalytics.quick_stats.users.total}
                  subtitle="Registered accounts"
                  icon={<PeopleAltIcon />}
                  color={blue[500]}
                  trend={
                    parseFloat(
                      dashboardAnalytics.quick_stats.users.growth_rate
                    ) > 0
                      ? "up"
                      : "down"
                  }
                  trendValue={`${dashboardAnalytics.quick_stats.users.growth_rate}% growth`}
                  progress={75}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  title="Active Rooms"
                  value={dashboardAnalytics.quick_stats.rooms.active}
                  subtitle="Currently active"
                  icon={<MeetingRoomIcon />}
                  color={green[500]}
                  progress={
                    dashboardAnalytics.quick_stats.rooms.total > 0
                      ? Math.round(
                          (dashboardAnalytics.quick_stats.rooms.active /
                            dashboardAnalytics.quick_stats.rooms.total) *
                            100
                        )
                      : 0
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  title="Total Revenue"
                  value={formatCurrency(
                    dashboardAnalytics.quick_stats.revenue.total
                  )}
                  subtitle="All time earnings"
                  icon={<AttachMoneyIcon />}
                  color={amber[600]}
                  trend="up"
                  trendValue="↗ This month"
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  title="Transactions"
                  value={dashboardAnalytics.quick_stats.revenue.transactions}
                  subtitle="Completed payments"
                  icon={<TimelineIcon />}
                  color={purple[500]}
                  progress={85}
                />
              </Grid>
            </>
          )}

          {/* System Health Dashboard */}
          {systemHealth && (
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  bgcolor: "white",
                  borderRadius: 4,
                  border: "1px solid #e3f2fd",
                  background:
                    "linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
                  <Avatar
                    sx={{ bgcolor: indigo[500], mr: 2, width: 48, height: 48 }}
                  >
                    <AnalyticsIcon />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: "#1a1a1a",
                        mb: 0.5,
                      }}
                    >
                      System Health Monitor
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Real-time system performance metrics
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} lg={3}>
                    <Card
                      sx={{
                        p: 3,
                        textAlign: "center",
                        bgcolor: `${indigo[500]}08`,
                        border: `1px solid ${indigo[500]}20`,
                        borderRadius: 3,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: 4,
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: indigo[500],
                          mx: "auto",
                          mb: 2,
                          width: 56,
                          height: 56,
                          boxShadow: 3,
                        }}
                      >
                        <ComputerIcon sx={{ fontSize: 28 }} />
                      </Avatar>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 700, color: indigo[700], mb: 1 }}
                      >
                        {systemHealth.uptime}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", fontWeight: 500 }}
                      >
                        Server Uptime
                      </Typography>
                      <Chip
                        label="Excellent"
                        size="small"
                        sx={{
                          mt: 1,
                          bgcolor: green[100],
                          color: green[800],
                          fontWeight: 600,
                        }}
                      />
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} lg={3}>
                    <Card
                      sx={{
                        p: 3,
                        textAlign: "center",
                        bgcolor: `${teal[500]}08`,
                        border: `1px solid ${teal[500]}20`,
                        borderRadius: 3,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: 4,
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: teal[500],
                          mx: "auto",
                          mb: 2,
                          width: 56,
                          height: 56,
                          boxShadow: 3,
                        }}
                      >
                        <StorageIcon sx={{ fontSize: 28 }} />
                      </Avatar>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 700, color: teal[700], mb: 1 }}
                      >
                        {systemHealth.database.total_records.toLocaleString()}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", fontWeight: 500 }}
                      >
                        Total Records
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={65}
                        sx={{
                          mt: 1,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: `${teal[500]}20`,
                          "& .MuiLinearProgress-bar": { bgcolor: teal[500] },
                        }}
                      />
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} lg={3}>
                    <Card
                      sx={{
                        p: 3,
                        textAlign: "center",
                        bgcolor: `${orange[500]}08`,
                        border: `1px solid ${orange[500]}20`,
                        borderRadius: 3,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: 4,
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: orange[500],
                          mx: "auto",
                          mb: 2,
                          width: 56,
                          height: 56,
                          boxShadow: 3,
                        }}
                      >
                        <AdminPanelSettingsIcon sx={{ fontSize: 28 }} />
                      </Avatar>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 700, color: orange[700], mb: 1 }}
                      >
                        {systemHealth.activity_24h.active_admins}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", fontWeight: 500 }}
                      >
                        Active Admins (24h)
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mt: 1,
                        }}
                      >
                        {[1, 2, 3, 4, 5].map((dot, index) => (
                          <Box
                            key={index}
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor:
                                index < systemHealth.activity_24h.active_admins
                                  ? orange[500]
                                  : orange[200],
                              mx: 0.25,
                            }}
                          />
                        ))}
                      </Box>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} lg={3}>
                    <Card
                      sx={{
                        p: 3,
                        textAlign: "center",
                        bgcolor: `${red[500]}08`,
                        border: `1px solid ${red[500]}20`,
                        borderRadius: 3,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: 4,
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: red[500],
                          mx: "auto",
                          mb: 2,
                          width: 56,
                          height: 56,
                          boxShadow: 3,
                        }}
                      >
                        <SecurityIcon sx={{ fontSize: 28 }} />
                      </Avatar>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 700, color: red[700], mb: 1 }}
                      >
                        {systemHealth.memory.used_mb}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", fontWeight: 500 }}
                      >
                        Memory Usage (MB)
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: red[600], fontWeight: 600 }}
                      >
                        /{systemHealth.memory.total_mb} MB
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Recent Activity Feed */}
          {dashboardAnalytics?.recent_activity &&
            Array.isArray(dashboardAnalytics.recent_activity) && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    bgcolor: "white",
                    borderRadius: 4,
                    border: "1px solid #e8f5e8",
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #f8fff8 100%)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 4,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Avatar
                        sx={{
                          bgcolor: green[500],
                          mr: 2,
                          width: 48,
                          height: 48,
                        }}
                      >
                        <AutoGraphIcon />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: "#1a1a1a",
                            mb: 0.5,
                          }}
                        >
                          Recent Activity Feed
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Latest administrative actions and system events
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={`${dashboardAnalytics.recent_activity.length} activities`}
                      color="success"
                      variant="outlined"
                    />
                  </Box>

                  <Box
                    sx={{
                      maxHeight: 500,
                      overflowY: "auto",
                      borderRadius: 3,
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    {dashboardAnalytics.recent_activity.map(
                      (activity, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            p: 3,
                            borderBottom:
                              index <
                              dashboardAnalytics.recent_activity.length - 1
                                ? "1px solid #f5f5f5"
                                : "none",
                            bgcolor: index % 2 === 0 ? "white" : "#fafafa",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: "#e3f2fd",
                              transform: "translateX(8px)",
                              borderLeft: `4px solid ${blue[500]}`,
                              paddingLeft: "20px",
                            },
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: blue[500],
                              mr: 3,
                              width: 44,
                              height: 44,
                              boxShadow: 2,
                            }}
                          >
                            <AdminPanelSettingsIcon fontSize="small" />
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 600,
                                color: "#1a1a1a",
                                mb: 0.5,
                              }}
                            >
                              <Box
                                component="span"
                                sx={{ color: blue[700], fontWeight: 700 }}
                              >
                                {activity.admin}
                              </Box>{" "}
                              {activity.description}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#666" }}>
                              {new Date(activity.timestamp).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}
                            </Typography>
                          </Box>
                          <Chip
                            label={activity.action}
                            size="small"
                            sx={{
                              bgcolor: blue[50],
                              color: blue[700],
                              fontWeight: 600,
                              border: `1px solid ${blue[200]}`,
                              borderRadius: 2,
                            }}
                          />
                        </Box>
                      )
                    )}
                  </Box>
                </Paper>
              </Grid>
            )}
        </Grid>
      </Container>
    );
  };

  const renderUserAnalytics = () => {
    if (loading) {
      return (
        <Container maxWidth="xl">
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        </Container>
      );
    }

    if (!userAnalytics) {
      return (
        <Container maxWidth="xl">
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary">
              No user analytics data available
            </Typography>
          </Paper>
        </Container>
      );
    }

    return (
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          {/* User Overview Stats */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Users"
              value={userAnalytics.overview.total_users}
              icon={<PeopleAltIcon />}
              color={blue[500]}
              trend={
                parseFloat(userAnalytics.growth.growth_rate) > 0 ? "up" : "down"
              }
              trendValue={`${userAnalytics.growth.growth_rate}%`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Active Users"
              value={userAnalytics.overview.active_users}
              icon={<PersonIcon />}
              color={green[500]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Verified Users"
              value={userAnalytics.overview.verified_users}
              icon={<SecurityIcon />}
              color={teal[500]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Verification Rate"
              value={formatPercentage(userAnalytics.overview.verification_rate)}
              icon={<BarChartIcon />}
              color={purple[500]}
            />
          </Grid>

          {/* Registration Trends Chart */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{ p: 4, borderRadius: 3, border: "1px solid #e0e0e0" }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 600, mb: 3 }}
              >
                Registration Trends
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={userAnalytics.trends || []}>
                  <defs>
                    <linearGradient
                      id="colorRegistrations"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor={blue[500]}
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="95%"
                        stopColor={blue[500]}
                        stopOpacity={0.05}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#666" }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#666" }}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="registrations"
                    stroke={blue[500]}
                    strokeWidth={3}
                    fill="url(#colorRegistrations)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Account Type Distribution */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{ p: 4, borderRadius: 3, border: "1px solid #e0e0e0" }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 600, mb: 3 }}
              >
                Account Types
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={userAnalytics.distribution || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="count"
                    label={({ account_type, percentage }) =>
                      `${account_type}: ${percentage}%`
                    }
                  >
                    {(userAnalytics.distribution || []).map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    );
  };

  const renderRoomAnalytics = () => {
    if (loading) {
      return (
        <Container maxWidth="xl">
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        </Container>
      );
    }

    if (!roomAnalytics) {
      return (
        <Container maxWidth="xl">
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary">
              No room analytics data available
            </Typography>
          </Paper>
        </Container>
      );
    }

    return (
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          {/* Room Overview Stats */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Rooms"
              value={roomAnalytics.overview.total_rooms}
              icon={<MeetingRoomIcon />}
              color={blue[500]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Active Rooms"
              value={roomAnalytics.overview.active_rooms}
              icon={<PublicIcon />}
              color={green[500]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Group Rooms"
              value={roomAnalytics.overview.group_rooms}
              icon={<GroupIcon />}
              color={purple[500]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Activity Rate"
              value={formatPercentage(roomAnalytics.overview.activity_rate)}
              icon={<BarChartIcon />}
              color={amber[600]}
            />
          </Grid>

          {/* Room Creation Trends */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{ p: 4, borderRadius: 3, border: "1px solid #e0e0e0" }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 600, mb: 3 }}
              >
                Room Creation Trends
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <RechartsBarChart data={roomAnalytics.trends || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#666" }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#666" }}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Bar
                    dataKey="rooms_created"
                    fill={blue[500]}
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="group_rooms"
                    fill={green[500]}
                    radius={[4, 4, 0, 0]}
                  />
                </RechartsBarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Top Rooms */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{ p: 4, borderRadius: 3, border: "1px solid #e0e0e0" }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 600, mb: 3 }}
              >
                Top Rooms by Participants
              </Typography>
              <Box sx={{ maxHeight: 350, overflowY: "auto" }}>
                {(roomAnalytics.top_rooms || [])
                  .slice(0, 8)
                  .map((room, index) => (
                    <Box
                      key={room.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        py: 2,
                        px: 1,
                        borderBottom:
                          index <
                          Math.min(roomAnalytics.top_rooms.length, 8) - 1
                            ? "1px solid #f0f0f0"
                            : "none",
                        borderRadius: 2,
                        "&:hover": {
                          bgcolor: "#f8f9fa",
                          transform: "translateX(4px)",
                          transition: "all 0.2s ease",
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor:
                            room.type === "group" ? purple[500] : green[500],
                          mr: 2,
                          width: 36,
                          height: 36,
                        }}
                      >
                        {room.type === "group" ? (
                          <GroupIcon fontSize="small" />
                        ) : (
                          <PersonIcon fontSize="small" />
                        )}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "#1a1a1a" }}
                        >
                          {room.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {room.type === "group"
                            ? "Group Chat"
                            : "Direct Message"}
                        </Typography>
                      </Box>
                      <Chip
                        label={`${room.participant_count}`}
                        size="small"
                        sx={{
                          bgcolor: `${purple[500]}10`,
                          color: purple[700],
                          fontWeight: 600,
                          minWidth: 40,
                        }}
                      />
                    </Box>
                  ))}
              </Box>
            </Paper>
          </Grid>

          {/* Participation Stats */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{ p: 4, borderRadius: 3, border: "1px solid #e0e0e0" }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 600, mb: 4 }}
              >
                Participation Statistics
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      p: 3,
                      textAlign: "center",
                      bgcolor: `${teal[500]}08`,
                      borderRadius: 3,
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: teal[500],
                        mx: "auto",
                        mb: 2,
                        width: 56,
                        height: 56,
                      }}
                    >
                      <PeopleAltIcon />
                    </Avatar>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: teal[700], mb: 1 }}
                    >
                      {roomAnalytics.participation?.unique_participants?.toLocaleString() ||
                        0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Unique Participants
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      p: 3,
                      textAlign: "center",
                      bgcolor: `${indigo[500]}08`,
                      borderRadius: 3,
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: indigo[500],
                        mx: "auto",
                        mb: 2,
                        width: 56,
                        height: 56,
                      }}
                    >
                      <TimelineIcon />
                    </Avatar>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: indigo[700], mb: 1 }}
                    >
                      {roomAnalytics.participation?.total_participations?.toLocaleString() ||
                        0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Participations
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      p: 3,
                      textAlign: "center",
                      bgcolor: `${orange[500]}08`,
                      borderRadius: 3,
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: orange[500],
                        mx: "auto",
                        mb: 2,
                        width: 56,
                        height: 56,
                      }}
                    >
                      <BarChartIcon />
                    </Avatar>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: orange[700], mb: 1 }}
                    >
                      {roomAnalytics.participation?.total_participations &&
                      roomAnalytics.participation?.unique_participants
                        ? (
                            roomAnalytics.participation.total_participations /
                            roomAnalytics.participation.unique_participants
                          ).toFixed(1)
                        : "0"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg Rooms per User
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      p: 3,
                      textAlign: "center",
                      bgcolor: `${red[500]}08`,
                      borderRadius: 3,
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: red[500],
                        mx: "auto",
                        mb: 2,
                        width: 56,
                        height: 56,
                      }}
                    >
                      <MeetingRoomIcon />
                    </Avatar>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: red[700], mb: 1 }}
                    >
                      {roomAnalytics.overview?.total_rooms &&
                      roomAnalytics.participation?.unique_participants
                        ? (
                            roomAnalytics.participation.unique_participants /
                            roomAnalytics.overview.total_rooms
                          ).toFixed(1)
                        : "0"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg Users per Room
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    );
  };

  const renderPaymentAnalytics = () => {
    if (loading) {
      return (
        <Container maxWidth="xl">
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        </Container>
      );
    }

    if (!paymentAnalytics) {
      return (
        <Container maxWidth="xl">
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary">
              No payment analytics data available
            </Typography>
          </Paper>
        </Container>
      );
    }

    return (
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          {/* Payment Overview Stats */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Revenue"
              value={formatCurrency(paymentAnalytics.overview.total_revenue)}
              icon={<AttachMoneyIcon />}
              color={green[600]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Payments"
              value={
                paymentAnalytics.overview.total_payments?.toLocaleString() || 0
              }
              icon={<TimelineIcon />}
              color={blue[500]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Success Rate"
              value={formatPercentage(paymentAnalytics.overview.success_rate)}
              icon={<CheckCircleIcon />}
              color={teal[500]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Average Amount"
              value={formatCurrency(
                paymentAnalytics.overview.avg_payment_amount
              )}
              icon={<TrendingUpIcon />}
              color={purple[500]}
            />
          </Grid>

          {/* Payment Status Overview */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{ p: 4, borderRadius: 3, border: "1px solid #e0e0e0" }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 600, mb: 4 }}
              >
                Payment Status Overview
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      p: 3,
                      textAlign: "center",
                      bgcolor: `${green[500]}08`,
                      borderRadius: 3,
                    }}
                  >
                    <Avatar sx={{ bgcolor: green[500], mx: "auto", mb: 2 }}>
                      <CheckCircleIcon />
                    </Avatar>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, color: green[700], mb: 1 }}
                    >
                      {paymentAnalytics.overview.completed_payments?.toLocaleString() ||
                        0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completed
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      p: 3,
                      textAlign: "center",
                      bgcolor: `${amber[600]}08`,
                      borderRadius: 3,
                    }}
                  >
                    <Avatar sx={{ bgcolor: amber[600], mx: "auto", mb: 2 }}>
                      <WarningIcon />
                    </Avatar>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, color: amber[700], mb: 1 }}
                    >
                      {paymentAnalytics.overview.pending_payments?.toLocaleString() ||
                        0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pending
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      p: 3,
                      textAlign: "center",
                      bgcolor: `${red[500]}08`,
                      borderRadius: 3,
                    }}
                  >
                    <Avatar sx={{ bgcolor: red[500], mx: "auto", mb: 2 }}>
                      <ErrorIcon />
                    </Avatar>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, color: red[700], mb: 1 }}
                    >
                      {paymentAnalytics.overview.failed_payments?.toLocaleString() ||
                        0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Failed
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      p: 3,
                      textAlign: "center",
                      bgcolor: `${blue[500]}08`,
                      borderRadius: 3,
                    }}
                  >
                    <Avatar sx={{ bgcolor: blue[500], mx: "auto", mb: 2 }}>
                      <StarBorderIcon />
                    </Avatar>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, color: blue[700], mb: 1 }}
                    >
                      {formatPercentage(paymentAnalytics.overview.success_rate)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Success Rate
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Payment Methods */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{ p: 4, borderRadius: 3, border: "1px solid #e0e0e0" }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 600, mb: 3 }}
              >
                Payment Methods
              </Typography>
              {paymentAnalytics.payment_methods &&
              Array.isArray(paymentAnalytics.payment_methods) ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={paymentAnalytics.payment_methods}
                      cx="50%"
                      cy="50%"
                      innerRadius={30}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="count"
                      label={({ method, percentage }) =>
                        `${method}: ${percentage}%`
                      }
                    >
                      {paymentAnalytics.payment_methods.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No payment method data available
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Revenue Trends */}
          {paymentAnalytics.trends &&
            Array.isArray(paymentAnalytics.trends) && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{ p: 4, borderRadius: 3, border: "1px solid #e0e0e0" }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 3 }}
                  >
                    Revenue Trends
                  </Typography>
                  <ResponsiveContainer width="100%" height={350}>
                    <AreaChart data={paymentAnalytics.trends}>
                      <defs>
                        <linearGradient
                          id="colorRevenue"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={green[600]}
                            stopOpacity={0.3}
                          />
                          <stop
                            offset="95%"
                            stopColor={green[600]}
                            stopOpacity={0.05}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="date"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: "#666" }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: "#666" }}
                      />
                      <RechartsTooltip
                        formatter={(value) => [
                          formatCurrency(value),
                          "Revenue",
                        ]}
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #e0e0e0",
                          borderRadius: "8px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke={green[600]}
                        strokeWidth={3}
                        fill="url(#colorRevenue)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            )}
        </Grid>
      </Container>
    );
  };

  const renderLoginActivity = () => {
    if (loading) {
      return (
        <Container maxWidth="xl">
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        </Container>
      );
    }

    if (!loginActivity) {
      return (
        <Container maxWidth="xl">
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary">
              No login activity data available
            </Typography>
          </Paper>
        </Container>
      );
    }

    return (
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          {/* Login Overview Stats */}
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Total Logins"
              value={loginActivity.overview.total_logins?.toLocaleString() || 0}
              icon={<SecurityIcon />}
              color={blue[500]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Unique Admins"
              value={
                loginActivity.overview.unique_admins?.toLocaleString() || 0
              }
              icon={<AdminPanelSettingsIcon />}
              color={purple[500]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Password Resets"
              value={
                loginActivity.overview.password_resets?.toLocaleString() || 0
              }
              icon={<LockIcon />}
              color={amber[600]}
            />
          </Grid>

          {/* Login Trends */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{ p: 4, borderRadius: 3, border: "1px solid #e0e0e0" }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 600, mb: 3 }}
              >
                Login Activity Trends
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={loginActivity.trends || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#666" }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#666" }}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="logins"
                    stroke={blue[500]}
                    strokeWidth={3}
                    name="Logins"
                    dot={{ fill: blue[500], strokeWidth: 2, r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="unique_admins"
                    stroke={purple[500]}
                    strokeWidth={3}
                    name="Unique Admins"
                    dot={{ fill: purple[500], strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Most Active Admins */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{ p: 4, borderRadius: 3, border: "1px solid #e0e0e0" }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 600, mb: 3 }}
              >
                Most Active Admins
              </Typography>
              <Box sx={{ maxHeight: 350, overflowY: "auto" }}>
                {(loginActivity.active_admins || [])
                  .slice(0, 8)
                  .map((admin, index) => (
                    <Box
                      key={admin.email}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        py: 2,
                        px: 1,
                        borderBottom:
                          index <
                          Math.min(loginActivity.active_admins.length, 8) - 1
                            ? "1px solid #f0f0f0"
                            : "none",
                        borderRadius: 2,
                        "&:hover": {
                          bgcolor: "#f8f9fa",
                          transform: "translateX(4px)",
                          transition: "all 0.2s ease",
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor:
                            admin.role === "super_admin" ? red[500] : blue[500],
                          mr: 2,
                          width: 36,
                          height: 36,
                        }}
                      >
                        <AdminPanelSettingsIcon fontSize="small" />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "#1a1a1a" }}
                        >
                          {admin.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          {admin.email}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 700, color: blue[600] }}
                        >
                          {admin.login_count}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          logins
                        </Typography>
                      </Box>
                    </Box>
                  ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    );
  };

  const renderContent = () => {
    switch (selectedAnalytics) {
      case "users":
        return renderUserAnalytics();
      case "rooms":
        return renderRoomAnalytics();
      case "payments":
        return renderPaymentAnalytics();
      case "login":
        return renderLoginActivity();
      default:
        return renderOverview();
    }
  };

  return (
    <Box>
      {/* Header Section */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, color: "#1976d2", mb: 1 }}
          >
            Analytics Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor system performance, user activity, and business metrics in real-time
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Tooltip title="Refresh All Data" arrow>
            <IconButton
              color="primary"
              onClick={handleRefresh}
              disabled={loading}
              sx={{
                "&:hover": {
                  transform: "rotate(180deg)",
                  transition: "all 0.5s ease",
                },
              }}
            >
              <RefreshIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 3,
            border: "1px solid #ffcdd2",
          }}
        >
          {error}
        </Alert>
      )}

      {/* Enhanced Controls */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          bgcolor: "white",
          borderRadius: 2,
          border: "1px solid #e0e0e0",
        }}
      >
        <Grid container spacing={3} alignItems="center">
          {/* Analytics Type Selection */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel sx={{ fontWeight: 600, color: "#424242" }}>
                📊 Analytics Type
              </InputLabel>
              <Select
                value={selectedAnalytics}
                label="📊 Analytics Type"
                onChange={(e) => handleAnalyticsChange(e.target.value)}
              >
                <MenuItem value="overview">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AutoGraphIcon fontSize="small" />
                    Overview Dashboard
                  </Box>
                </MenuItem>
                <MenuItem value="users">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PeopleAltIcon fontSize="small" />
                    User Analytics
                  </Box>
                </MenuItem>
                <MenuItem value="rooms">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <MeetingRoomIcon fontSize="small" />
                    Room Analytics
                  </Box>
                </MenuItem>
                <MenuItem value="payments">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AttachMoneyIcon fontSize="small" />
                    Payment Analytics
                  </Box>
                </MenuItem>
                <MenuItem value="login">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SecurityIcon fontSize="small" />
                    Login Activity
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Date Range Preset */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel sx={{ fontWeight: 600, color: "#424242" }}>
                📅 Time Period
              </InputLabel>
              <Select
                value={dateRange.preset}
                label="📅 Time Period"
                onChange={(e) =>
                  setDateRange((prev) => ({
                    ...prev,
                    preset: e.target.value,
                  }))
                }
              >
                <MenuItem value="7d">📊 Last 7 days</MenuItem>
                <MenuItem value="30d">📈 Last 30 days</MenuItem>
                <MenuItem value="90d">📉 Last 90 days</MenuItem>
                <MenuItem value="1y">📋 Last year</MenuItem>
                <MenuItem value="custom">🎯 Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Custom Date Range */}
          {dateRange.preset === "custom" && (
            <>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  value={dateRange.dateFrom}
                  onChange={(e) =>
                    handleDateRangeChange("dateFrom", e.target.value)
                  }
                  InputLabelProps={{
                    shrink: true,
                    sx: { fontWeight: 600, color: "#424242" },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  value={dateRange.dateTo}
                  onChange={(e) =>
                    handleDateRangeChange("dateTo", e.target.value)
                  }
                  InputLabelProps={{
                    shrink: true,
                    sx: { fontWeight: 600, color: "#424242" },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  variant="contained"
                  startIcon={<DateRangeIcon />}
                  onClick={handleApplyDateRange}
                  disabled={loading}
                  fullWidth
                  sx={{ height: "56px", fontWeight: 600 }}
                >
                  Apply Range
                </Button>
              </Grid>
            </>
          )}

          {/* Loading Indicator */}
          {loading && (
            <Grid item xs={12} md={dateRange.preset === "custom" ? 12 : 6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading analytics data...
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Analytics Content */}
      <Box sx={{ bgcolor: "transparent" }}>{renderContent()}</Box>
    </Box>
  );
};

export default AnalyticsDashboard;
